import React, {
  useEffect, useRef, useCallback, useState,
} from 'react';
import {
  ErrorBoundary,
  SearchProvider,
  WithSearch,
} from '@elastic/react-search-ui';
import {
  Layout,
} from '@elastic/react-search-ui-views';
import { createJobListingConnector } from '@scripts/frontend/elastic/proxy-connector';
import { __ } from '@wordpress/i18n';
import SearchHeader from '../header/SearchHeader';
import MobileFilterMenu from '../mobile/MobileFilterMenu';
import FilterSummary from '../facet/FilterSummary';
import CustomPaginationView from '../pagination/CustomPaginationView';
import { MobileSearchProvider } from '../mobile/MobileSearchContext';

import FacetContainer from '../customFacet';
import { getRegionLabels } from '../facet/regionLabels';
import { getInitialMarket, getInitialRegion } from '../facet/urlLocale';
import { ElasticResultsWrapper } from '../job/ElasticResultsWrapper';

const connector = createJobListingConnector();

// Whether a market was pre-selected on load, used to open the Location facet by default.
const hasInitialMarket = !!getInitialMarket();

// Creates the initial filters for the search query based on the current locale and URL path.
const createInitialFilters = () => {
  const market = getInitialMarket();
  const filters = [];

  if (market) {
    filters.push({
      field: 'market.keyword',
      values: [market],
      type: 'any',
    });
  }

  filters.push({
    field: 'region.keyword',
    values: [getInitialRegion()],
    type: 'any',
  });

  return filters;
};

// Creates autocomplete filters based on region and brand
const getAutocompleteFilters = () => {
  const filters = [...createInitialFilters()];
  const region = window.aqJobsOptions?.region;
  if (region && region !== 'all') {
    filters.push({
      field: 'region.keyword',
      values: [region, 'all'],
      type: 'any',
    });
  }
  const brand = window.aqJobsOptions?.brand;
  if (brand && brand !== 'all') {
    filters.push({
      field: 'brand.keyword',
      values: [brand, 'all'],
      type: 'any',
    });
  }

  return filters;
};

const config = {
  searchQuery: {
    search_fields: {
      job_title: { weight: 5 }, // prioritize job_title
      job_description: { weight: 1 },
    },
    result_fields: {
      job_title: { raw: {} },
      job_description: { snippet: {} },
      location: { raw: {} },
      salary: { raw: {} },
      job_id: { raw: {} },
      posted_date: { raw: {} },
      offsite_preference: { raw: {} },
      friendly_salary: { raw: {} },
    },
    disjunctiveFacets: [
      'market.keyword',
      'job_hours.keyword',
      'placement_type.keyword',
      'offsite_preference.keyword',
      'region.keyword',
    ],
    facets: {
      'market.keyword': {
        type: 'value', sort: 'value', direction: 'asc', size: 1000,
      },
      'job_hours.keyword': { type: 'value' },
      'placement_type.keyword': { type: 'value' },
      'offsite_preference.keyword': { type: 'value' },
      'region.keyword': { type: 'value' },
    },
  },
  initialState: {
    filters: createInitialFilters(),
    resultsPerPage: 12,
  },
  autocompleteQuery: {
    results: {
      filters: getAutocompleteFilters(),
      search_fields: { job_title: { weight: 3 } },
      result_fields: {
        job_title: { snippet: { size: 100, fallback: true } },
      },
    },
  },
  apiConnector: connector,
  alwaysSearchOnInitialLoad: true,
  trackUrlState: true,
};

const JobListingMain = () => {
  const [isReady, setIsReady] = useState(false);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);

      // If the URL has no sort parameters, we inject them manually BEFORE the app boots
      if (!params.has('sort[0][field]')) {
        const hasQuery = params.has('q') && params.get('q') !== '';

        if (!hasQuery) {
          // Newest jobs first
          params.set('sort[0][field]', 'posted_date');
          params.set('sort[0][direction]', 'desc');
        } else {
          // Has search query: Relevance first, then newest
          params.set('sort[0][field]', '_score');
          params.set('sort[0][direction]', 'desc');
          params.set('sort[1][field]', 'posted_date');
          params.set('sort[1][direction]', 'desc');
        }

        // update the URL without reloading the page so that SearchProvider picks up the new sort parameters on initial load
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(null, '', newUrl);
      }
    }
    // Now that the URL is seeded with the correct sort parameters, we can allow the app to load
    setIsReady(true);
  }, []);

  const scrollToResultsTop = useCallback(() => {
    if (resultsContainerRef.current) {
      resultsContainerRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, []);

  // Prevent loading the UI until the URL is seeded
  if (!isReady) return null;

  return (
    <SearchProvider config={config}>
      <MobileSearchProvider>
        <WithSearch
          mapContextToProps={({
            setSearchTerm,
            searchTerm,
            setSort,
          }) => ({
            setSearchTerm,
            searchTerm,
            setSort,
          })}
        >
          {({
            setSearchTerm,
            searchTerm,
            setSort,
          }) => {
            // Track when the user actively types to change the sort dynamically
            const prevSearchTerm = useRef(searchTerm);

            useEffect(() => {
              if (searchTerm !== prevSearchTerm.current) {
                const desiredSort = !searchTerm
                  ? [{ field: 'posted_date', direction: 'desc' }]
                  : [
                    { field: '_score', direction: 'desc' },
                    { field: 'posted_date', direction: 'desc' },
                  ];

                setSort(desiredSort);
                prevSearchTerm.current = searchTerm;
              }
            }, [searchTerm, setSort]);

            return (
              <div className="job-search">
                <SearchHeader setSearchTerm={setSearchTerm} searchTerm={searchTerm} />
                <ErrorBoundary>
                  <Layout
                    header={<MobileFilterMenu />}
                    sideContent={
                      <div className="job-listing-sidebar">
                        <FilterSummary />
                        <FacetContainer key={'5'} field={'region.keyword'} label={__('Region', 'wp-plugin-jobs')} onFilterChange={scrollToResultsTop} valueLabels={getRegionLabels()} defaultOpen />
                        <FacetContainer key={'1'} field={'market.keyword'} label={__('Location', 'wp-plugin-jobs')} onFilterChange={scrollToResultsTop} defaultOpen={hasInitialMarket} />
                        <FacetContainer key={'6'} field={'job_hours.keyword'} label={__('Hours', 'wp-plugin-jobs')} onFilterChange={scrollToResultsTop} />
                        <FacetContainer key={'2'} field={'placement_type.keyword'} label={__('Placement type', 'wp-plugin-jobs')} onFilterChange={scrollToResultsTop} />
                        <FacetContainer key={'4'} field={'offsite_preference.keyword'} label={__('Location preference', 'wp-plugin-jobs')} onFilterChange={scrollToResultsTop} />
                      </div>
                    }
                    bodyContent={
                      <div className="job-listing-body-results" ref={resultsContainerRef}>
                        <ElasticResultsWrapper />
                      </div>
                    }
                    bodyFooter={
                      <div className="job-listing-body-footer">
                        <CustomPaginationView
                          pagingContainer="pagination"
                          resultsContainerRef={resultsContainerRef}
                        />
                      </div>
                    }
                  />
                </ErrorBoundary>
              </div>
            );
          }}
        </WithSearch>
      </MobileSearchProvider>
    </SearchProvider>
  );
};

export { JobListingMain };
